import React, { useState } from 'react';
import { testIds } from '../../lib/testIds';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FONT = "var(--xp-font-body)";

type TopicKey =
  | 'getting-started'
  | 'document-library'
  | 'prompt-lab'
  | 'timeline-diff'
  | 'keyboard-shortcuts'
  | 'about';

interface TopicNode {
  key: TopicKey;
  label: string;
  children?: TopicNode[];
}

const TOPICS: TopicNode[] = [
  { key: 'getting-started', label: 'Getting Started' },
  {
    key: 'document-library',
    label: 'Document Library',
  },
  {
    key: 'prompt-lab',
    label: 'Prompt Lab',
  },
  {
    key: 'timeline-diff',
    label: 'Timeline & Diff',
  },
  { key: 'keyboard-shortcuts', label: 'Keyboard Shortcuts' },
  { key: 'about', label: 'About Repancy' },
];

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  display: 'flex',
  height: '100%',
  fontFamily: FONT,
  fontSize: 12,
  overflow: 'hidden',
};

const tocStyle: React.CSSProperties = {
  width: 180,
  minWidth: 140,
  borderStyle: 'solid',
  borderWidth: 2,
  background: '#fff',
  overflowY: 'auto',
  flexShrink: 0,
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  padding: '10px 14px',
  overflowY: 'auto',
  borderStyle: 'solid',
  borderWidth: 2,
  background: '#fff',
  marginLeft: 6,
};

function tocItemStyle(active: boolean): React.CSSProperties {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '4px 8px',
    background: active ? 'var(--xp-selection)' : 'transparent',
    color: active ? '#fff' : '#000',
    border: 'none',
    fontFamily: FONT,
    fontSize: 11,
    cursor: 'pointer',
    lineHeight: 1.6,
  };
}

const headingStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 'bold',
  marginBottom: 8,
  color: 'var(--xp-selection)',
  borderBottom: '1px solid #808080',
  paddingBottom: 4,
};

const paragraphStyle: React.CSSProperties = {
  marginBottom: 8,
  lineHeight: 1.7,
  color: '#000',
};

const kbdStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 5px',
  background: '#e0e0e0',
  borderStyle: 'solid',
  borderWidth: 1,
  fontFamily: "var(--xp-font-mono), monospace",
  fontSize: 11,
  borderRadius: 2,
  marginRight: 2,
};

const shortcutRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '4px 0',
  borderBottom: '1px dotted #ece9d8',
};

const listStyle: React.CSSProperties = {
  paddingLeft: 20,
  lineHeight: 2,
  marginBottom: 8,
};

// ---------------------------------------------------------------------------
// Content panels
// ---------------------------------------------------------------------------

function GettingStarted() {
  return (
    <div>
      <div style={headingStyle}>Getting Started</div>
      <p style={paragraphStyle}>
        Welcome to Repancy, a legal AI assistant built to help attorneys draft, analyze,
        and manage legal documents using large language models.
      </p>
      <p style={paragraphStyle}>
        The desktop interface mimics a Windows 95 environment. Double-click desktop icons
        or use the taskbar to open application windows.
      </p>
      <p style={paragraphStyle}><strong>Quick start:</strong></p>
      <ol style={listStyle}>
        <li>Open <strong>Document Library</strong> to browse matter folders and documents.</li>
        <li>Select a document and click <strong>Send to Prompt Lab</strong>.</li>
        <li>Choose a model, write your prompt, and click <strong>Run</strong>.</li>
        <li>Review the output and click <strong>Save Output</strong> to save back to the library.</li>
      </ol>
      <p style={paragraphStyle}>
        Configure your OpenRouter API key in <strong>Settings &gt; API Keys</strong> to
        enable live model inference.
      </p>
    </div>
  );
}

function DocumentLibrary() {
  return (
    <div>
      <div style={headingStyle}>Document Library</div>
      <p style={paragraphStyle}>
        The Document Library provides a SharePoint-style explorer for browsing legal
        documents organized by client and matter.
      </p>
      <p style={paragraphStyle}><strong>Features:</strong></p>
      <ul style={listStyle}>
        <li><strong>Tree navigation</strong> - Expand clients, then matters, then documents.</li>
        <li><strong>Document preview</strong> - Select a document to see its content, metadata, and version info.</li>
        <li><strong>Filtering</strong> - Filter by document type, confidentiality level, or search by title.</li>
        <li><strong>Drag-and-drop</strong> - Drag documents from the library into the Prompt Lab document context area.</li>
        <li><strong>Send to Prompt Lab</strong> - Click the button in the preview pane to load a document as context.</li>
        <li><strong>Version History</strong> - View all versions of a document with change descriptions.</li>
      </ul>
      <p style={paragraphStyle}>
        Documents are tagged with confidentiality levels: Public (green), Confidential (orange),
        Attorney-Client Privileged (red), and Work Product (navy).
      </p>
    </div>
  );
}

function PromptLab() {
  return (
    <div>
      <div style={headingStyle}>Prompt Lab</div>
      <p style={paragraphStyle}>
        The Prompt Lab is where you interact with LLMs to analyze, draft, and transform
        legal documents.
      </p>
      <p style={paragraphStyle}><strong>Session workflow:</strong></p>
      <ol style={listStyle}>
        <li><strong>System Prompt</strong> - Set the AI's role and instructions (e.g., "You are a corporate attorney specializing in M&A").</li>
        <li><strong>Skill Prompts</strong> - Drag pre-built skill cards into slots for common tasks (contract review, due diligence, redlining).</li>
        <li><strong>User Prompt</strong> - Write your specific request or question.</li>
        <li><strong>Document Context</strong> - Attach documents from the library as reference material.</li>
        <li><strong>Run</strong> - Execute the prompt against the selected model.</li>
      </ol>
      <p style={paragraphStyle}><strong>Model selection:</strong></p>
      <p style={paragraphStyle}>
        Use the model dropdown to choose from available LLMs. Models show provider, context window size,
        and pricing. Mark favorites in Settings &gt; Models for quick access.
      </p>
      <p style={paragraphStyle}><strong>Saving output:</strong></p>
      <p style={paragraphStyle}>
        Click <strong>Save Output</strong> in the toolbar to open the Save dialog. You can save
        as a new document in any matter folder, or as a new version of the source document.
        Provenance metadata (source, model, session) is tracked automatically.
      </p>
    </div>
  );
}

function TimelineDiff() {
  return (
    <div>
      <div style={headingStyle}>Timeline & Diff</div>
      <p style={paragraphStyle}>
        The Timeline window lets you scrub through prompt session history and compare
        outputs across different steps.
      </p>
      <p style={paragraphStyle}><strong>Features:</strong></p>
      <ul style={listStyle}>
        <li><strong>Timeline scrubber</strong> - Drag the slider to move through session steps.</li>
        <li><strong>Forking</strong> - Fork from any point in the timeline to create a branch with different parameters.</li>
        <li><strong>Diff view</strong> - Compare two steps side-by-side with additions and deletions highlighted.</li>
        <li><strong>Step metadata</strong> - See token counts, model used, and timestamps for each step.</li>
      </ul>
      <p style={paragraphStyle}>
        Toggle diff mode with <span style={kbdStyle}>Ctrl</span>+<span style={kbdStyle}>D</span> to
        quickly compare the current output with a previous version.
      </p>
    </div>
  );
}

function KeyboardShortcuts() {
  const shortcuts: { keys: string[]; desc: string }[] = [
    { keys: ['Ctrl', 'N'], desc: 'New prompt session' },
    { keys: ['Ctrl', 'Enter'], desc: 'Run prompt' },
    { keys: ['Ctrl', 'S'], desc: 'Save output' },
    { keys: ['Ctrl', 'D'], desc: 'Toggle diff view' },
    { keys: ['Ctrl', '←'], desc: 'Snap window left' },
    { keys: ['Ctrl', '→'], desc: 'Snap window right' },
    { keys: ['Escape'], desc: 'Close dialog / menu' },
  ];

  return (
    <div>
      <div style={headingStyle}>Keyboard Shortcuts</div>
      <div style={{ marginBottom: 12 }}>
        {shortcuts.map((s, i) => (
          <div key={i} style={shortcutRowStyle}>
            <span>
              {s.keys.map((k, ki) => (
                <span key={ki}>
                  <span style={kbdStyle}>{k}</span>
                  {ki < s.keys.length - 1 && <span style={{ margin: '0 2px' }}>+</span>}
                </span>
              ))}
            </span>
            <span style={{ color: '#333' }}>{s.desc}</span>
          </div>
        ))}
      </div>
      <p style={paragraphStyle}>
        Keyboard shortcuts are active when the relevant window is focused.
        Shortcuts that conflict with browser defaults may require the window
        to be in full focus.
      </p>
    </div>
  );
}

function AboutRepancy() {
  return (
    <div>
      <div style={headingStyle}>About Repancy</div>
      <div style={{
        textAlign: 'center',
        padding: '20px 0',
      }}>
        <div style={{ fontSize: 18, fontWeight: 'bold', color: 'var(--xp-selection)', marginBottom: 4 }}>
          Repancy
        </div>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>
          Legal AI Document Assistant
        </div>
        <div style={{
          borderStyle: 'solid',
          borderWidth: 2,
          background: '#fff',
          padding: 12,
          margin: '0 auto',
          maxWidth: 280,
          textAlign: 'left',
          lineHeight: 1.8,
        }}>
          <div><strong>Version:</strong> 0.1.0-preview</div>
          <div><strong>Build:</strong> 2026.05.13</div>
          <div><strong>Runtime:</strong> React 19 + Redux Toolkit</div>
          <div><strong>LLM Backend:</strong> OpenRouter API</div>
          <div><strong>Database:</strong> Convex</div>
          <div style={{ borderTop: '1px solid #e0e0e0', marginTop: 8, paddingTop: 8 }}>
            <strong>Credits:</strong>
            <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
              Built by E Duncan
            </div>
            <div style={{ fontSize: 10, color: '#666' }}>
              Win95 aesthetic by nostalgia
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const CONTENT_MAP: Record<TopicKey, () => React.JSX.Element> = {
  'getting-started': GettingStarted,
  'document-library': DocumentLibrary,
  'prompt-lab': PromptLab,
  'timeline-diff': TimelineDiff,
  'keyboard-shortcuts': KeyboardShortcuts,
  'about': AboutRepancy,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function HelpWindow() {
  const [activeTopic, setActiveTopic] = useState<TopicKey>('getting-started');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const Content = CONTENT_MAP[activeTopic];

  function toggleCollapse(key: string) {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <div style={containerStyle} data-testid={testIds.help.root}>
      {/* Left pane: TOC */}
      <div style={tocStyle} data-testid={testIds.help.tocTree}>
        <div style={{
          background: 'var(--xp-selection)',
          color: '#fff',
          padding: '3px 6px',
          fontWeight: 'bold',
          fontSize: 11,
        }}>
          ? Help Topics
        </div>
        {TOPICS.map((topic) => (
          <div key={topic.key}>
            <button
              style={tocItemStyle(activeTopic === topic.key)}
              onClick={() => {
                setActiveTopic(topic.key);
                if (topic.children) toggleCollapse(topic.key);
              }}
              data-testid={testIds.help.tocItem(topic.key)}
            >
              {topic.children
                ? (collapsed[topic.key] ? '▶ ' : '▼ ')
                : '• '}
              {topic.label}
            </button>
            {topic.children && !collapsed[topic.key] && topic.children.map((child) => (
              <button
                key={child.key}
                style={{
                  ...tocItemStyle(activeTopic === child.key),
                  paddingLeft: 20,
                }}
                onClick={() => setActiveTopic(child.key)}
                data-testid={testIds.help.tocItem(child.key)}
              >
                {child.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Right pane: content */}
      <div style={contentStyle} data-testid={testIds.help.contentPane}>
        <Content />
      </div>
    </div>
  );
}
